import React, { useState, useEffect, useRef, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { selectThemeMode } from '../../../redux/diagram/diagram.selectors';

import { getPaddle } from '../../../billing/paddleClient';
import axios from '../../../axiosInstance';
import requests from '../../../requests';

import { selectIsModalPricingPlanUpgradeEnabled, selectModalPricingPlanUpgradeData } from '../../../redux/main/main.selectors';
import { togglePricingPlanUpgrade } from '../../../redux/main/main.actions';

import { selectAuthUser } from '../../../redux/auth/auth.selectors';


import { trackEvent } from '../../../lib/analytics';

import { CloseButtonIcon } from "../../Icon";

import Modal from '../Modal/Modal';

import './ModalPricingPlanUpgrade.scss';

const plansData = [
    {
        id: 'free',
        name: "Free",
        monthlyPrice: 0,
        annualPrice: 0,
        buttonText: "Change Plan",
        featuresHeader: "",
        features: [
            "Team workspace",
            "Unlimited members",
            "2 teams",
            "3 diagrams",
            "40 tables / diagram",
            // ["Common features", "(areas, notes, export, etc)"],
        ],
        priceMonthlyId: '',
        priceAnnuallyId: '',
    },
    {
        id: 'basic',
        name: "Basic",
        monthlyPrice: 14,
        annualPrice: 10,
        buttonText: "Change Plan",
        featuresHeader: "",
        features: [
            "All Free features +",
            "Unlimited members",
            "5 teams",
            "Unlimited diagrams",
            "Unlimited tables / diagram",
            "Version history (soon)",
            // "Comments (soon)"
        ],
        priceMonthlyId: process.env.REACT_APP_PADDLE_PRICE_BASIC_MONTHLY,
        priceAnnuallyId: process.env.REACT_APP_PADDLE_PRICE_BASIC_ANNUALLY,
    },
    {
        id: 'professional',
        name: "Professional",
        monthlyPrice: 20,
        annualPrice: 16,
        buttonText: "Change Plan",
        featuresHeader: "",
        features: [
            "All Basic features +",
            "Unlimited members",
            "Unlimited teams",
            "Priority support",
            "Admin roles",
            "Guest access (soon)",
            // "DB Design AI (soon)",
            // "Audit log (soon)",
        ],
        priceMonthlyId: process.env.REACT_APP_PADDLE_PRICE_PROFESSIONAL_MONTHLY,
        priceAnnuallyId: process.env.REACT_APP_PADDLE_PRICE_PROFESSIONAL_ANNUALLY,
    },
    // {
    //     id: 'enterprise',
    //     name: "Enterprise",
    //     monthlyPrice: 'Custom',
    //     annualPrice: 'Custom',
    //     buttonText: "Contact Us",
    //     featuresHeader: "Everything in team, plus",
    //     features: [
    //         "Customized plan",
    //         "High priority support",
    //         "Org-wide onboarding",
    //         "Org-wide training",
    //         "Audit Logs Management",
    //         "More Licences: $5 /month /user",
    //     ],
    // },
];

const plansMap = {};
plansData.forEach(plan => {
    plansMap[plan.id] = plan;
});

export const ModalPricingPlanUpgrade = () => {
    const dispatch = useDispatch();

    const currentUser = useSelector(selectAuthUser);

    const isOpen = useSelector(selectIsModalPricingPlanUpgradeEnabled);
    const modalData = useSelector(selectModalPricingPlanUpgradeData);

    const isLightTheme = useSelector(selectThemeMode);
    const selectedPlanId = 'free';

    const [page, setPage] = useState('');

    const [isSwitchYearHovered, setIsSwitchYearHovered] = useState(false);
    const [isSwitchMonthHovered, setIsSwitchMonthHovered] = useState(false);

    const [isAnnual, setIsAnnual] = useState(true);
    const [isAnnualDelayed, setIsAnnualDelayed] = useState(true);


    useEffect(() => {
        if (!modalData?.page) return;
        setPage(modalData?.page);

        return () => { };
    }, [modalData]);

    const handleCancel = () => {
        dispatch(togglePricingPlanUpgrade(null));
        setTimeout(() => {
            setIsAnnual(true);
            setIsAnnualDelayed(true);
        }, 300);
    };

    const handleTogglePricingSwitch = () => {
        trackEvent('app_modal_pricing_upgrade_switch', {
            page: 'app',
            type: 'modals',
        });

        setIsAnnual(!isAnnual);

        setTimeout(() => {
            setIsAnnualDelayed(!isAnnual);
        }, 200);
    };

    const handleSubmitClick = (planId, isAnnual) => {
        trackEvent('app_modal_pricing_upgrade_submit', {
            page: 'app',
            type: 'modals',
            subtype: `${planId}_${isAnnual ? 'annually' : 'monthly'}`,
        });

        openCheckout(planId, isAnnual);
        // dispatch(togglePricingPlanUpgrade(null));
    };

    // Prefetch Paddle по hover
    const handlePrefetch = useCallback(() => {
        getPaddle().catch(() => { });
    }, []);

    const openCheckout = useCallback(async (planId, isAnnual) => {
        const plan = plansMap[planId];
        if (!plan) {
            console.error("Missing plan:", planId, isAnnual);
            return;
        }

        const priceId = isAnnual ? plan.priceAnnuallyId : plan.priceMonthlyId;
        if (!priceId) {
            console.error("Missing priceId for plan:", planId, isAnnual);
            return;
        }

        let checkoutRef = null;
        try {
            const { data } = await axios.post(requests.checkoutRequest, { planId });
            checkoutRef = data?.checkout_ref ?? null;
        } catch (err) {
            console.error("Checkout request failed:", err);
            return;
        }

        const paddle = await getPaddle();
        paddle.Checkout.open({
            items: [{ priceId, quantity: 1 }],
            settings: {
                displayMode: "overlay",
                theme: "light",
                successUrl: `${window.location.origin}/upgrade/success`,
            },
            customer: { email: currentUser?.email || '' },
            customData: {
                checkout_ref: checkoutRef,
            },
        });
    }, [currentUser]);

    return (
        <Modal
            isModalFullScreen={true}
            isOpen={isOpen}
            modalClass={`modal-pricing-plans ${page} ${isLightTheme ? 'light' : ''}`}
            onCancel={handleCancel}
        // onShouldRender={handleRender}
        >
            <button className="close-button" onClick={handleCancel}>
                <CloseButtonIcon />
            </button>

            <div className="pricing-title">Upgrade your plan</div>
            <div className="pricing-subtitle">You can always upgrade or downgrade your plan later</div>

            <div className='pricing-switch'>
                <span className={`pricing-switch-highlight ${(isSwitchYearHovered && isAnnual) || (isSwitchMonthHovered && !isAnnual) ? 'hovered' : ''} ${isAnnual ? 'annually' : 'monthly'}`}></span>
                <button
                    className={`pricing-switch-btn monthly ${!isAnnual ? 'selected' : ''}`}
                    onMouseEnter={() => setIsSwitchMonthHovered(true)}
                    onMouseLeave={() => setIsSwitchMonthHovered(false)}
                    onClick={handleTogglePricingSwitch}
                >Monthly</button>
                <button
                    className={`pricing-switch-btn annually ${isAnnual ? 'selected' : ''}`}
                    onMouseEnter={() => setIsSwitchYearHovered(true)}
                    onMouseLeave={() => setIsSwitchYearHovered(false)}
                    onClick={handleTogglePricingSwitch}
                >Annually</button>
            </div>

            <div className="pricing-cards-group">
                {plansData.map((plan) => {
                    const isFreePlan = plan.id === 'free';
                    const isEnterprisePlan = plan.id === 'enterprise';

                    const price = isAnnualDelayed ? plan.annualPrice : plan.monthlyPrice;
                    const billingText =
                        isFreePlan
                            ? 'Free for everyone'
                            : isAnnualDelayed
                                ? "Billed annually"
                                : "Billed monthly";

                    return (
                        <div className={`pricing-card ${plan.id === 'basic' ? 'selected' : ''}`} key={plan.id}>
                            <div className="pricing-card-title">{plan.name}</div>
                            <div className="pricing-card-price-section">
                                {isAnnualDelayed && !isFreePlan && !isEnterprisePlan ? <div className="pricing-card-price-cross">{`${isEnterprisePlan ? plan.monthlyPrice : `$${plan.monthlyPrice}`}`}</div> : ''}
                                <div className="pricing-card-price">{`${isEnterprisePlan ? price : `$${price}`}`}</div>
                                {/* <div className="pricing-card-price-period">/month</div> */}
                                {!isFreePlan && !isEnterprisePlan ? <div className="pricing-card-price-period">/user/month</div> : ''}

                            </div>
                            <div className="pricing-card-additional-text">{billingText}</div>

                            <hr className='horizontal-line' />

                            <button
                                className={`pricing-card-selection-button ${selectedPlanId === plan.id ? 'selected' : ''}`}
                                onMouseEnter={handlePrefetch}
                                onClick={() => { handleSubmitClick(plan.id, isAnnual); }}
                            >{selectedPlanId === plan.id ? 'Change Plan' : plan.buttonText}</button>

                            <ul className="pricing-card-features">
                                {plan.featuresHeader ?
                                    <li className='pricing-card-features-item features-header'>
                                        <span>{plan.featuresHeader}</span>
                                    </li>
                                    : ''
                                }

                                {plan.features.map((feature, index) => (
                                    <li key={index} className='pricing-card-features-item'>
                                        {
                                            // (plan.id === 'free' && [2, 3].includes(index)) ||
                                            (plan.id === 'basic' && [5, 6].includes(index)) ||
                                                (plan.id === 'professional' && [5, 6, 7].includes(index))
                                                // soon
                                                // ? <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '-2px', fill: 'none' }}>
                                                //     <path d="M12 8V12L15 15" stroke="#b6dcfd" strokeWidth="1.8" strokeLinecap="round"></path>
                                                //     <circle cx="12" cy="12" r="9" stroke="#b6dcfd" strokeWidth="1.8"></circle>
                                                // </svg>

                                                // ? <svg className="icon-check soon" viewBox="0 0 20 20" fill="#96a4b9">
                                                //     <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd"></path>
                                                // </svg>
                                                // // check
                                                // : <svg className="icon-check" viewBox="0 0 20 20">
                                                //     <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd"></path>
                                                // </svg>

                                                ? <svg className="icon-check soon" viewBox="0 0 20 20"><path fillRule="evenodd" clipRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"></path></svg>
                                                : <svg className="icon-check" viewBox="0 0 20 20"><path fillRule="evenodd" clipRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"></path></svg>
                                        }
                                        <span>
                                            {Array.isArray(feature) ? (<>{feature[0]}<br />{feature[1]}</>) : (feature)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};

export default ModalPricingPlanUpgrade;


























// export const ModalPricingPlanUpgrade = () => {
//     const inputRef = useRef(null);

//     const isLightTheme = useSelector(selectThemeMode);

//     const selectedPlanId = 'free';

//     useEffect(() => {
//         if (isOpen && inputRef.current) {
//             inputRef.current.focus();
//         }
//     }, [isOpen]);

//     const dispatch = useDispatch();

//     const [name, setName] = useState('');
//     const [description, setDescription] = useState('');

//     const handleSave = () => {
//     };

//     const handleCancel = () => {
//     };

//     useEffect(() => {
//         const handleKeyPress = (event) => {
//             if (!isOpen) return;

//             if (event.code === 'Escape') {
//                 event.preventDefault();

//                 handleCancel();
//             }
//         };

//         document.addEventListener('keydown', handleKeyPress);
//         return () => {
//             document.removeEventListener('keydown', handleKeyPress);
//         };
//     }, [isOpen, handleCancel]);

//     const [isSwitchYearHovered, setIsSwitchYearHovered] = useState(false);
//     const [isSwitchMonthHovered, setIsSwitchMonthHovered] = useState(false);

//     const [isAnnual, setIsAnnual] = useState(true);
//     const [isAnnualDelayed, setIsAnnualDelayed] = useState(true);

//     const plansData = [
//         {
//             id: 'free',
//             name: "Free",
//             monthlyPrice: 0,
//             annualPrice: 0,
//             buttonText: "Get Free",
//             featuresHeader: "",
//             features: [
//                 "3 Private Diagrams",
//                 "40 Tables / Diagram",
//                 "Sticky Notes",
//                 "Areas & Table Groups",
//                 '“Keys Only” View',
//                 "Pen Drawing",
//                 "Export as Image",
//                 "Export as SQL",
//             ],
//         },
//         {
//             id: 'personal',
//             name: "Personal",
//             monthlyPrice: 14,
//             annualPrice: 10,
//             buttonText: "Get Personal",
//             featuresHeader: "Everything in Free, plus",
//             features: [
//                 "Unlimited Private Diagrams",
//                 "Unlimited Tables / Diagram",
//                 "Version History",
//                 "Comments / Threads",
//                 "Export as Documentation",
//                 "Live User Collaboration",
//                 "Live User Collaboration",
//             ],
//         },
//         {
//             id: 'business',
//             name: "Business",
//             monthlyPrice: 70,
//             annualPrice: 50,
//             buttonText: "Get Business",
//             featuresHeader: "Everything in Personal, plus",
//             features: [
//                 "5 User Licences",
//                 "Team Workspace",
//                 // Team Pricing Plan
//                 // - (old) Team Workspace
//                 // - (new) Workspaces & Teams & Projects
//                 "Unified Access Management",
//                 "Unified Billing Management",
//                 "Audit Logs Management",
//                 "More Licences: $5 /month /user",
//             ],
//         },
//         // {
//         //     id: 'enterprise',
//         //     name: "Enterprise",
//         //     monthlyPrice: 'Custom',
//         //     annualPrice: 'Custom',
//         //     buttonText: "Contact Us",
//         //     featuresHeader: "Everything in Business, plus",
//         //     features: [
//         //         "Customized plan",
//         //         "High priority support",
//         //         "Org-wide onboarding",
//         //         "Org-wide training",
//         //         "Audit Logs Management",
//         //         "More Licences: $5 /month /user",
//         //     ],
//         // },
//     ];

//     const handleTogglePricingSwitch = () => {
//         setIsAnnual(!isAnnual);

//         setTimeout(() => {
//             setIsAnnualDelayed(!isAnnual);
//         }, 200);
//     };




//     if (!isOpen) return null;


//     return (
//         // <div className={`modal-upgrade-plan ${isLightTheme ? 'merged' : ''}`}>
//         <div className={`modal-upgrade-plan ${isLightTheme ? 'light' : ''}`}>
//             <div className="modal-overlay"></div>
//             <div className="modal-content">
//                 <div className='pricing-wrap'>
//                     <button className="close-button" onClick={handleCancel}>
//                         <CloseButtonIcon />
//                     </button>

//                     <div className="pricing-title">Upgrade your plan</div>
//                     <div className="pricing-subtitle">Choose a plan that works best for you. You can always upgrade or downgrade your plan later.</div>

//                     <div className='pricing-switch'>
//                         <span className={`pricing-switch-highlight ${(isSwitchYearHovered && isAnnual) || (isSwitchMonthHovered && !isAnnual) ? 'hovered' : ''} ${isAnnual ? 'yearly' : 'monthly'}`}></span>
//                         <button
//                             className={`pricing-switch-btn yearly ${isAnnual ? 'selected' : ''}`}
//                             onMouseEnter={() => setIsSwitchYearHovered(true)}
//                             onMouseLeave={() => setIsSwitchYearHovered(false)}
//                             onClick={handleTogglePricingSwitch}
//                         >Annually <span>-30%</span></button>
//                         <button
//                             className={`pricing-switch-btn monthly ${!isAnnual ? 'selected' : ''}`}
//                             onMouseEnter={() => setIsSwitchMonthHovered(true)}
//                             onMouseLeave={() => setIsSwitchMonthHovered(false)}
//                             onClick={handleTogglePricingSwitch}
//                         >Monthly</button>
//                     </div>

//                     <div className="pricing-cards-group">
//                         {plansData.map((plan) => {
//                             const isFreePlan = plan.id === 'free';
//                             const isEnterprisePlan = plan.id === 'enterprise';

//                             const price = isAnnualDelayed ? plan.annualPrice : plan.monthlyPrice;
//                             const billingText =
//                                 isFreePlan
//                                     ? 'Free for everyone'
//                                     : isAnnualDelayed
//                                         ? "Billed annually"
//                                         : "Billed monthly";

//                             return (
//                                 <div className="pricing-card" key={plan.id}>
//                                     <div className="pricing-card-title">{plan.name}</div>
//                                     <div className="pricing-card-price-section">
//                                         {isAnnualDelayed && !isFreePlan && !isEnterprisePlan ? <div className="pricing-card-price-cross">{`${isEnterprisePlan ? plan.monthlyPrice : `$${plan.monthlyPrice}`}`}</div> : ''}
//                                         <div className="pricing-card-price">{`${isEnterprisePlan ? price : `$${price}`}`}</div>
//                                         {/* <div className="pricing-card-price-period">/month</div> */}
//                                         {!isFreePlan && !isEnterprisePlan ? <div className="pricing-card-price-period">/month</div> : ''}

//                                     </div>
//                                     <div className="pricing-card-additional-text">{billingText}</div>

//                                     <hr className='horizontal-line' />

//                                     <button className={`pricing-card-selection-button ${selectedPlanId === plan.id ? 'selected' : ''}`}>{selectedPlanId === plan.id ? 'Selected plan' : plan.buttonText}</button>

//                                     <ul className="pricing-card-features">
//                                         {plan.featuresHeader ?
//                                             <li className='pricing-card-features-item features-header'>
//                                                 <span>{plan.featuresHeader}</span>
//                                             </li>
//                                             : ''
//                                         }

//                                         {plan.features.map((feature, index) => (
//                                             <li key={index} className='pricing-card-features-item'>
//                                                 <svg className="icon-check" viewBox="0 0 20 20">
//                                                     <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd"></path>
//                                                 </svg>
//                                                 <span>{feature}</span>
//                                             </li>
//                                         ))}
//                                     </ul>
//                                 </div>
//                             );
//                         })}
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default ModalPricingPlanUpgrade;



